import { Directionality } from "@angular/cdk/bidi";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzTagComponent } from "ng-zorro-antd/tag";
import { Assignee, UnknownAssignee } from "@webapp/assignees/models/assignee.models";
import { UiChipIcon, UiChipMode, UiChipType } from "./models/chip.models";

/**
 * This component extends the base NzTag component of NG-Zorro.
 * Example usage: https://ng.ant.design/components/tag/en#ng-content.
 * DS epic: https://gtmhub.atlassian.net/browse/GVS-16793.
 * Confluence page: https://gtmhub.atlassian.net/wiki/spaces/DS/pages/2405892338/Chip.
 *
 * @param uiDisabled boolean (default: false)
 * @param uiAssignee IAssignee
 * @param uiCloseable boolean (default: false)
 * @param uiDotColor string (default: @teal-8)
 * @param tabindex number | string | null (default: 0)
 * @param uiIcon UiChipIcon
 */

@Component({
  selector: "ui-chip",
  exportAs: "uiChip",
  templateUrl: "./chip.component.html",
  styleUrls: ["chip.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "ui-chip",
    "[class.ui-chip-assignee]": `assignee`,
    "[class.ui-chip-dissmisable]": `this.nzMode === "closeable" && this.showCloseIcon`,
    "[class.ui-chip-hide-name]": `this.shouldHideName`,
    "[class.ui-chip-truncated]": `this.shouldTruncateName`,
    "[class.ui-chip-disabled]": `disabled`,
    "[attr.tabindex]": "tabindex",
    "[attr.aria-disabled]": "disabled",
  },
})
export class UiChipComponent extends NzTagComponent implements AfterViewInit, OnChanges {
  @ViewChild("assigneeChip") private assigneeChip: TemplateRef<unknown>;
  @ViewChild("dotChip") private dotChip: TemplateRef<unknown>;
  @ViewChild("iconChip") private iconChip: TemplateRef<unknown>;

  @HostListener("keydown.backspace", ["$event"])
  @HostListener("keydown.delete", ["$event"])
  public handleDelete(event): void {
    if (this.nzMode === "closeable") {
      this.closeTag(event);
    }
  }

  @Input("uiAssignee")
  public assignee?: Assignee | UnknownAssignee;

  @Input("uiCloseable")
  @InputBoolean()
  public set closeable(closeable: boolean) {
    this.nzMode = closeable ? "closeable" : "default";
  }

  @Input("uiDisabled")
  @InputBoolean()
  public disabled?: boolean;

  @Input("uiShowCloseIcon")
  @InputBoolean()
  public showCloseIcon?: boolean;

  @Input("uiDotColor")
  public dotColor?: string;

  @Input("uiIcon")
  public icon?: UiChipIcon;

  @Input("tabindex")
  public tabindex: number | string | null = 0;

  public type: UiChipType;
  public chipTemplateRef: TemplateRef<unknown>;
  public nzMode: UiChipMode = "default";
  public shouldHideName: boolean;
  public shouldTruncateName: boolean;

  @Output("uiOnClose")
  public readonly nzOnClose = new EventEmitter<MouseEvent>();

  private change: ChangeDetectorRef = null;
  private elementReference: ElementRef = null;

  constructor(cdr: ChangeDetectorRef, renderer: Renderer2, elementRef: ElementRef, @Optional() directionality: Directionality) {
    super(cdr, renderer, elementRef, directionality);
    this.change = cdr;
    this.elementReference = elementRef;
  }
  public ngOnChanges(changes: SimpleChanges): void {
    if (this.nzMode === "closeable" && changes.showCloseIcon) {
      if (changes.showCloseIcon.currentValue) {
        // We need to firstly remove the class in order to get correct calculation for the overflow in shouldHideAssigneeName()
        this.elementReference.nativeElement.classList.remove("ui-chip-truncated");
        this.shouldTruncateName = false;
        this.shouldHideName = this.shouldHideAssigneeName();
      } else if (!changes.showCloseIcon.currentValue) {
        this.shouldTruncateName = true;
        this.shouldHideName = false;
      }
    }
  }

  public ngAfterViewInit(): void {
    if (this.assignee) {
      this.type = "assignee";
    } else if (this.icon) {
      this.type = "icon";
    } else if (this.dotColor) {
      this.type = "dot";
    }

    this.chipTemplateRef = this.chipTemplate;
    this.change.detectChanges();
  }

  private get chipTemplate(): TemplateRef<unknown> {
    const templates = {
      assignee: this.assigneeChip,
      icon: this.iconChip,
      dot: this.dotChip,
    };

    return templates[this.type];
  }

  private shouldHideAssigneeName(): boolean {
    const elementOffsetWidth = this.elementReference.nativeElement.offsetWidth;
    const parentClientWidth = this.elementReference.nativeElement.parentElement.clientWidth;
    return parentClientWidth < elementOffsetWidth;
  }
}
